import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MediaLightbox from '../components/MediaLightbox';
import { getWorkItem } from '../services/api';
import { ArrowLeft, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';

const WorkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workItem, setWorkItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxMedia, setLightboxMedia] = useState([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center' });
  const [emblaRefVideos, emblaApiVideos] = useEmblaCarousel({ loop: true, align: 'center' });

  useEffect(() => {
    fetchWorkItem();
  }, [id]);

  const fetchWorkItem = async () => {
    try {
      const data = await getWorkItem(id);
      setWorkItem(data);
    } catch (err) {
      console.error('Error fetching work item:', err);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollPrevVideos = useCallback(() => {
    if (emblaApiVideos) emblaApiVideos.scrollPrev();
  }, [emblaApiVideos]);

  const scrollNextVideos = useCallback(() => {
    if (emblaApiVideos) emblaApiVideos.scrollNext();
  }, [emblaApiVideos]);

  // Auto-fullscreen handler for iframes
  const handleIframeClick = useCallback((e) => {
    const iframe = e.currentTarget;
    if (iframe.requestFullscreen) {
      iframe.requestFullscreen();
    } else if (iframe.webkitRequestFullscreen) {
      iframe.webkitRequestFullscreen();
    } else if (iframe.mozRequestFullScreen) {
      iframe.mozRequestFullScreen();
    } else if (iframe.msRequestFullscreen) {
      iframe.msRequestFullscreen();
    }
  }, []);

  const openLightbox = (mediaArray, index) => {
    setLightboxMedia(mediaArray);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setLightboxIndex((prev) => (prev === 0 ? lightboxMedia.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setLightboxIndex((prev) => (prev === lightboxMedia.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!workItem) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <Header />
      
      <main className="pt-28 md:pt-32 pb-16 px-8">
        <div className="max-w-[1200px] mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 group md:mt-2"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">BACK TO WORK</span>
          </button>
          
          <div className="text-center mb-12">
            <h2 className="text-white font-['Poppins'] text-5xl md:text-6xl font-bold mb-6">
              {workItem.title}
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div 
                className="h-1 w-16 rounded-full"
                style={{ backgroundColor: workItem.border_color }}
              />
              <p className="text-gray-400 text-lg tracking-wider uppercase">
                {workItem.role}
              </p>
              <div 
                className="h-1 w-16 rounded-full"
                style={{ backgroundColor: workItem.border_color }}
              />
            </div>
          </div>
          
          <div className="space-y-8">
            {/* Main Image - Scaled Down */}
            {workItem.show_image !== false && (
              <div
                className="overflow-hidden rounded-2xl border-2 shadow-2xl max-w-4xl mx-auto cursor-pointer group"
                style={{ borderColor: workItem.border_color, boxShadow: `0 0 40px ${workItem.border_color}40` }}
                onClick={() => {
                  const allMedia = [workItem.image_url];
                  if (workItem.show_video !== false && workItem.video_url && !workItem.video_url.includes('vimeo.com') && !workItem.video_url.includes('youtube.com') && !workItem.video_url.includes('youtu.be')) {
                    allMedia.push(workItem.video_url);
                  }
                  if (workItem.additional_images && workItem.additional_images.length > 0) {
                    allMedia.push(...workItem.additional_images);
                  }
                  openLightbox(allMedia, 0);
                }}
              >
                <img
                  src={workItem.image_url}
                  alt={workItem.title}
                  className="w-full h-auto max-h-[600px] object-contain bg-black/20 group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            )}

            {/* Primary Video Section */}
            {workItem.show_video !== false && workItem.video_url && (
              <div className="glass-effect rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Play className="text-purple-400" size={24} />
                  <h3 className="text-white font-['Poppins'] text-2xl font-bold">
                    PROJECT VIDEO
                  </h3>
                </div>
                <div className="aspect-video rounded-xl overflow-hidden border border-white/10">
                  {workItem.video_url.includes('vimeo.com') || workItem.video_url.includes('youtube.com') || workItem.video_url.includes('youtu.be') ? (
                    <iframe
                      src={workItem.video_url}
                      className="w-full h-full cursor-pointer"
                      allowFullScreen
                      title={`${workItem.title} video`}
                      onClick={handleIframeClick}
                    ></iframe>
                  ) : (
                    <video
                      src={workItem.video_url}
                      controls
                      className="w-full h-full"
                    >
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {workItem.description && (
              <div className="glass-effect rounded-2xl p-8">
                <h3 className="text-white font-['Poppins'] text-2xl font-bold mb-6">
                  ABOUT THIS PROJECT
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-wrap">
                  {workItem.description}
                </p>
              </div>
            )}

            {/* Additional Images Gallery with Carousel */}
            {workItem.additional_images && workItem.additional_images.length > 0 && (
              <div className="glass-effect rounded-2xl p-8">
                <h3 className="text-white font-['Poppins'] text-2xl font-bold mb-6">
                  PROJECT GALLERY
                </h3>
                <div className="relative">
                  <div className="overflow-hidden rounded-xl" ref={emblaRef}>
                    <div className="flex">
                      {workItem.additional_images.map((img, index) => (
                        <div
                          key={index}
                          className="flex-[0_0_100%] min-w-0 px-2"
                        >
                          <div
                            className="overflow-hidden rounded-xl border-2 cursor-pointer group"
                            style={{ borderColor: workItem.border_color, boxShadow: `0 0 20px ${workItem.border_color}40` }}
                            onClick={() => openLightbox(workItem.additional_images, index)}
                          >
                            <img
                              src={img}
                              alt={`${workItem.title} - Image ${index + 1}`}
                              className="w-full h-auto max-h-[600px] object-contain bg-black/20 group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Arrow Navigation Buttons */}
                  {workItem.additional_images.length > 1 && (
                    <>
                      <button
                        onClick={scrollPrev}
                        className="absolute left-4 top-1/2 -translate-y-1/2 glass-effect p-3 rounded-full border border-white/20 text-white hover:bg-white/10 hover:scale-110 transition-all duration-300 shadow-lg z-10"
                        style={{ boxShadow: `0 0 20px ${workItem.border_color}60` }}
                        aria-label="Previous image"
                      >
                        <ChevronLeft size={28} />
                      </button>
                      <button
                        onClick={scrollNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 glass-effect p-3 rounded-full border border-white/20 text-white hover:bg-white/10 hover:scale-110 transition-all duration-300 shadow-lg z-10"
                        style={{ boxShadow: `0 0 20px ${workItem.border_color}60` }}
                        aria-label="Next image"
                      >
                        <ChevronRight size={28} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Additional Vimeo Videos with Carousel */}
            {workItem.vimeo_embeds && workItem.vimeo_embeds.length > 0 && (
              <div className="glass-effect rounded-2xl p-8">
                <h3 className="text-white font-['Poppins'] text-2xl font-bold mb-6">
                  MORE VIDEOS
                </h3>
                <div className="relative">
                  <div className="overflow-hidden rounded-xl" ref={emblaRefVideos}>
                    <div className="flex">
                      {workItem.vimeo_embeds.map((embed, index) => (
                        <div
                          key={index}
                          className="flex-[0_0_100%] min-w-0 px-2"
                        >
                          <div className="aspect-video rounded-xl overflow-hidden border border-white/10">
                            <iframe
                              src={embed}
                              className="w-full h-full cursor-pointer"
                              allowFullScreen
                              title={`${workItem.title} - Video ${index + 1}`}
                              onClick={handleIframeClick}
                            ></iframe>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Arrow Navigation Buttons for Videos */}
                  {workItem.vimeo_embeds.length > 1 && (
                    <>
                      <button
                        onClick={scrollPrevVideos}
                        className="absolute left-4 top-1/2 -translate-y-1/2 glass-effect p-3 rounded-full border border-white/20 text-white hover:bg-white/10 hover:scale-110 transition-all duration-300 shadow-lg z-10"
                        style={{ boxShadow: `0 0 20px ${workItem.border_color}60` }}
                        aria-label="Previous video"
                      >
                        <ChevronLeft size={28} />
                      </button>
                      <button
                        onClick={scrollNextVideos}
                        className="absolute right-4 top-1/2 -translate-y-1/2 glass-effect p-3 rounded-full border border-white/20 text-white hover:bg-white/10 hover:scale-110 transition-all duration-300 shadow-lg z-10"
                        style={{ boxShadow: `0 0 20px ${workItem.border_color}60` }}
                        aria-label="Next video"
                      >
                        <ChevronRight size={28} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />

      {/* Media Lightbox */}
      {lightboxOpen && (
        <MediaLightbox
          media={lightboxMedia}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrevious={goToPrevious}
          onNext={goToNext}
        />
      )}
    </div>
  );
};

export default WorkDetail;